/** Unique long-form copy for solution and service detail routes. */

export const FX_SOLUTION_DETAIL = {
  "forex-crm": {
    intro: "Run your entire client lifecycle from one command center built for FX operations.",
    paragraphs: [
      "Our CRM treats onboarding status, treasury movements, partner commissions, and support queues as one connected system — so nothing falls out of sync when volume spikes.",
      "Role boundaries, immutable activity logs, and approval paths are configured to match how compliance, sales, and operations teams actually collaborate.",
    ],
    highlights: [
      "Self-service portal for verification, funding, and withdrawals",
      "Multi-level partner trees with statement-ready payouts",
      "Risk indicators embedded beside each client record",
      "Connectors for bridges, payment service providers, and helpdesk tools",
    ],
  },
  "forex-web-design": {
    intro: "Publish broker websites that earn trust, rank cleanly, and move visitors toward funded accounts.",
    paragraphs: [
      "We structure pages around clarity: product tiers, regional disclosures, and demo requests placed where traders naturally look next.",
      "Visual language carries from public marketing into signed-in areas so the experience feels like one product, not two stitched sites.",
    ],
    highlights: [
      "React builds optimized for Core Web Vitals and search",
      "Region-specific content blocks for global campaigns",
      "Shared UI kit between marketing and authenticated zones",
      "Readable typography and accessibility checks baked in",
    ],
  },
  "liquidity-bridge": {
    intro: "Route orders to aggregated venues with measured latency and defined failover behavior.",
    paragraphs: [
      "Routing logic, margin validation, and session recovery are engineered for volatile sessions — reducing unexplained rejects during fast markets.",
      "Whether onboarding a new LP or migrating feeds, every hop is documented with monitoring your operations team can act on.",
    ],
    highlights: [
      "Cross-venue symbol mapping and session controls",
      "Dashboards for feed health and fill quality",
      "Phased LP onboarding with rollback plans",
      "Operational runbooks tied to your risk policy",
    ],
  },
  "prop-firm-crm": {
    intro: "Operate evaluations, scaling rules, and payout cycles with software shaped for prop desks.",
    paragraphs: [
      "Challenge parameters should live in product logic, not spreadsheets. We encode drawdowns, consistency checks, and payout tiers so support and risk read identical data.",
      "Transparent trader progress reduces disputes and shortens month-end reconciliation.",
    ],
    highlights: [
      "Auditable challenge progression states",
      "Payout approval queues with delegated roles",
      "Trader-facing progress and leaderboard views",
      "Integrations for KYC vendors and payout rails",
    ],
  },
  "forex-copier": {
    intro: "Mirror trades between leader and follower accounts with limits your risk officers can enforce.",
    paragraphs: [
      "Replication is both a growth feature and an exposure risk. Lot scaling, symbol filters, and book-level caps prevent silent concentration when popular leaders move size.",
      "We profile latency early — copy products only work when fills arrive consistently under stress.",
    ],
    highlights: [
      "Allocation modes: fixed lots, ratio, and equity-based",
      "Automatic pause on drift or connection loss",
      "Master/follower reporting for supervisory review",
      "Partner APIs for white-label distribution",
    ],
  },
  "dedicated-server": {
    intro: "Provision MT4 / MT5 and supporting services on hardware sized for your heaviest sessions.",
    paragraphs: [
      "Certain bridge and matching workloads still benefit from dedicated metal. Capacity planning uses your symbol mix and peak concurrency — not generic templates.",
      "Patching, backups, and observability are included in delivery so production ownership is straightforward.",
    ],
    highlights: [
      "Pre-launch performance baselines",
      "Actionable alerting on CPU, disk, and network",
      "Placement options across major regions",
      "Handover packs for internal infrastructure teams",
    ],
  },
  "backend-solutions": {
    intro: "Back-office consoles, payment gateways, and reporting that keep daily operations aligned.",
    paragraphs: [
      "Admin tooling should reflect your policies — pricing tiers, credit rules, and approvals — instead of forcing teams into generic screens.",
      "Finance-ready reporting links trading activity, CRM events, and treasury data so closing periods stay predictable.",
    ],
    highlights: [
      "Granular admin roles and permission scopes",
      "Payment and liquidity gateway patterns",
      "Scheduled exports and on-demand reports",
      "Modular services that grow with headcount",
    ],
  },
  "vps-hosting": {
    intro: "Host expert advisors and lightweight services on dependable virtual machines with clear SLAs.",
    paragraphs: [
      "Retail users expect automation to survive maintenance and provider faults. Snapshot schedules, patch communication, and health checks reduce avoidable support load.",
      "Instance sizing recommendations reflect realistic concurrent workloads — not oversold starter plans.",
    ],
    highlights: [
      "Images pre-tuned for common trading stacks",
      "Latency and bandwidth visibility",
      "Optional managed backup schedules",
      "Clear upgrade paths as usage grows",
    ],
  },
  "risk-management": {
    intro: "Surface exposure, concentration, and stress outcomes before they spread across the book.",
    paragraphs: [
      "Risk views belong where decisions happen: concise dashboards, routed alerts, and drill-downs that answer exposure by symbol and region in seconds.",
      "Metric definitions follow your internal policy language so audit reviews map directly to on-screen data.",
    ],
    highlights: [
      "Live exposure grouped by symbol, desk, and geography",
      "Shock and gap templates for scenario testing",
      "Alerts to Slack, email, or ticketing systems",
      "Historical views for incident analysis",
    ],
  },
  "custom-tools": {
    intro: "Deliver specialized utilities — internal ops panels, calculators, or trader-facing widgets.",
    paragraphs: [
      "When off-the-shelf products miss an edge case, we prototype quickly, harden under real traffic, and document extension points for your engineers.",
      "Implementation follows the same standards as our platform work: typed interfaces, observable jobs, and UI consistency with your brand.",
    ],
    highlights: [
      "Workshops with desk stakeholders to define scope",
      "Security review on sensitive data paths",
      "Automated checks on business-critical rules",
      "Deployment to your preferred cloud or on-prem",
    ],
  },
  consulting: {
    intro: "Validate roadmaps, vendor shortlists, and launch sequencing with teams that have shipped many desks.",
    paragraphs: [
      "Engagements focus on decisions: architecture tradeoffs, migration sequencing, and realistic build-versus-buy choices.",
      "Regulatory and commercial constraints become engineering milestones leadership can track week to week.",
    ],
    highlights: [
      "Technical and integration architecture reviews",
      "Structured vendor evaluation support",
      "Go-live readiness and cutover checklists",
      "Post-launch optimization sprints",
    ],
  },
  "crm-marketing-prop-firm": {
    intro: "Scale funded and prop offerings with marketing wired into CRM and compliance guardrails.",
    paragraphs: [
      "Trust depends on consistent messaging. Landing pages, lifecycle email, and in-app copy pull from the same rule set your CRM enforces.",
      "Campaign design supports evaluation, reset, and payout journeys without breaking attribution or support context.",
    ],
    highlights: [
      "Landing and nurture flows synced to CRM states",
      "Disclosure blocks aligned to region and program",
      "Attribution mapped to desk and partner reporting",
      "Launch playbooks for promos and seasonal pushes",
    ],
  },
  "crm-prop-form-tool": {
    intro: "Replace ad-hoc PDFs with validated forms that feed directly into CRM workflows.",
    paragraphs: [
      "Disconnected forms create reconciliation pain. We ship multi-step submissions, routing, and approvals visible to risk and support in one timeline.",
      "Business rules encode eligibility, drawdown checks, and payout conditions — cutting manual overrides.",
    ],
    highlights: [
      "Configurable steps with approval chains",
      "Versioned legal text and consent capture",
      "Webhooks into payouts, tickets, and risk engines",
      "Templates for resets, upgrades, and appeals",
    ],
  },
  manpower: {
    intro: "Embed specialists who already understand broker operations, risk, and platform tooling.",
    paragraphs: [
      "Hiring alone can mean months of domain ramp-up. We supply CRM operators, analysts, and engineers who work inside your stack and runbooks from day one.",
      "Engagements scale from launch surge support to ongoing fractional roles with defined handback plans.",
    ],
    highlights: [
      "Screened profiles across technology and operations",
      "Knowledge transfer to permanent hires",
      "Access patterns aligned to your security policy",
      "Documented exit when roles move in-house",
    ],
  },
  regulations: {
    intro: "Implement compliance requirements in software without turning product into legal advice.",
    paragraphs: [
      "We encode counsel-driven rules into behavior: consent capture, data minimization, residency options, and partner diligence artifacts.",
      "Integration catalogs document which vendors include which attestations — simplifying reviews for your compliance function.",
    ],
    highlights: [
      "KYC and AML states with full audit history",
      "Data collection limited to necessary fields",
      "Partner onboarding review templates",
      "Documentation bundles for supervisory exams",
    ],
  },
};

export const SERVICES_DETAIL = {
  "static-website-development": {
    intro: "Marketing sites that load quickly, index well, and present your offer with clarity.",
    paragraphs: [
      "Static architecture suits broker and funded brands that want predictable hosting costs and minimal attack surface.",
      "We deliver structured page hierarchies, conversion-focused calls to action, and analytics hooks planned before launch — not patched in later.",
    ],
    highlights: [
      "Pages tuned for search fundamentals and speed",
      "Reusable component library for future pages",
      "Funnel tracking aligned to campaign goals",
      "Deploy targets: Vercel, Netlify, S3, or your CDN",
    ],
  },
  "dynamic-web-platforms": {
    intro: "Authenticated web products for onboarding, dashboards, and ongoing client engagement.",
    paragraphs: [
      "When experiences need logins, forms, and live data, we build on a coherent UI system with typed service contracts.",
      "Stable state handling and production monitoring let your team ship improvements after go-live without guesswork.",
    ],
    highlights: [
      "Client and admin portals with role gates",
      "Validated workflows with audit-friendly history",
      "Integration patterns with explicit error handling",
      "Performance profiling before traffic ramps",
    ],
  },
  "e-commerce-builds": {
    intro: "Online storefronts focused on checkout completion and operational reliability.",
    paragraphs: [
      "Catalog structure, pricing logic, and checkout must stay consistent under promotions and refunds.",
      "We design measurable funnels and connect operational reporting where inventory or fulfillment matters.",
    ],
    highlights: [
      "Store UX optimized for conversion",
      "Payment, tax, and shipping hooks as required",
      "Order notifications and status tracking",
      "Export-friendly transaction data",
    ],
  },
  "android-app-development": {
    intro: "Android applications with responsive interaction and release discipline.",
    paragraphs: [
      "Apps mirror your brand system across onboarding, alerts, and account management while staying stable on mid-range devices.",
      "Offline behavior, secure credential storage, and crash telemetry are addressed before store submission.",
    ],
    highlights: [
      "Authentication flows with protected local storage",
      "API clients with graceful degradation",
      "Production crash and usage instrumentation",
      "Play Store packaging and update guidance",
    ],
  },
  "ios-app-development": {
    intro: "iOS products with polished motion, dependable releases, and maintainable codebases.",
    paragraphs: [
      "Navigation, animation, and responsiveness are tuned to Apple HIG expectations without sacrificing backend reliability.",
      "We support signing, review submission, and repeatable release trains for your internal team.",
    ],
    highlights: [
      "Consistent navigation and visual hierarchy",
      "Secure session and data handling",
      "Event tracking for funnel analysis",
      "App Store compliance and release automation",
    ],
  },
  "crm-consulting": {
    intro: "Clarify which CRM shape fits your desk before you commit to a rollout.",
    paragraphs: [
      "We interview stakeholders across onboarding, treasury, support, and partnerships — then map gaps against realistic vendor or custom options.",
      "Deliverables include workflow diagrams, integration sketches, and a phased implementation plan your team owns.",
    ],
    highlights: [
      "Process mapping and requirement capture",
      "Vendor comparison with weighted scoring",
      "Data model and integration review",
      "Cutover checklist with owners and dates",
    ],
  },
  "crm-implementation": {
    intro: "Configure CRM behavior around your policies, roles, and production cutover plan.",
    paragraphs: [
      "Implementation translates operating rules into permissions, statuses, and notifications each department can trust.",
      "Staged launches limit operational risk while keeping leadership visibility on progress.",
    ],
    highlights: [
      "Permission model aligned to org structure",
      "KYC, PSP, and bridge hooks where required",
      "Phased rollout with rollback steps",
      "Training materials and admin handover",
    ],
  },
  "crm-integration": {
    intro: "Synchronize CRM with trading, treasury, and analytics systems using observable pipelines.",
    paragraphs: [
      "Production integrations must handle retries, duplicate events, and partial failures without corrupting books.",
      "Dashboards and structured logs shorten mean time to diagnosis when something misbehaves overnight.",
    ],
    highlights: [
      "Queue and webhook patterns for durability",
      "Reconciliation jobs and replay tooling",
      "Monitoring hooks for on-call teams",
      "Maintainability docs for internal developers",
    ],
  },
};
